import React, { useMemo } from "react";
import { Button, Card, Empty, List, Skeleton, Space, Tag, Typography } from "antd";
import type { GlobalToken } from "antd/es/theme/interface";
import { ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { Tournament } from "../../../services/beerpongService";
import { TournamentStatus, BracketType } from "../../../generated/bpong_pb";
import { buildStatusMeta } from "./constants";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

type TournamentListProps = {
    tournaments: Tournament[];
    loading: boolean;
    onReload: () => void;
    selectedTournamentId: number | null;
    onSelectTournament: (tournament: Tournament) => void;
    token: GlobalToken;
};

const TournamentList: React.FC<TournamentListProps> = ({
    tournaments,
    loading,
    onReload,
    selectedTournamentId,
    onSelectTournament,
    token,
}) => {
    const { t } = useTranslation();
    const statusMeta = useMemo(() => buildStatusMeta(t), [t]);

    const renderWinner = (tournament: Tournament): string | null => {
        if (tournament.status !== TournamentStatus.TOURNAMENT_STATUS_COMPLETED) {
            return null;
        }

        const completedMatches = tournament.matches
            .filter((match) => !match.isCancelled && Boolean(match.winnerTeamId))
            .sort((a, b) => (a.roundNumber ?? 0) - (b.roundNumber ?? 0));

        if (completedMatches.length === 0) {
            return null;
        }

        const explicitFinal = completedMatches
            .slice()
            .reverse()
            .find((match) => match.bracketType === BracketType.BRACKET_TYPE_FINALS);

        const targetMatch = explicitFinal ?? completedMatches.at(-1);
        if (!targetMatch?.winnerTeamId) {
            const survivingTeam = tournament.teams.find((team) => !team.isEliminated);
            return survivingTeam?.teamName ?? null;
        }

        const winnerTeam = tournament.teams.find((team) => team.teamId === targetMatch.winnerTeamId);
        return winnerTeam?.teamName ?? null;
    };

    const renderItem = (tournament: Tournament) => {
        const statusInfo =
            statusMeta.get(tournament.status) ??
            statusMeta.get(TournamentStatus.TOURNAMENT_STATUS_ACTIVE)!;
        const winner = renderWinner(tournament);
        const isSelected = tournament.tournamentId === selectedTournamentId;

        return (
            <List.Item
                key={tournament.tournamentId}
                onClick={() => onSelectTournament(tournament)}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onSelectTournament(tournament);
                    }
                }}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                style={{
                    cursor: "pointer",
                    borderRadius: 8,
                    border: isSelected ? `1px solid ${token.colorPrimary}` : `1px solid ${token.colorBorderSecondary}`,
                    background: isSelected ? token.colorFillSecondary : token.colorBgElevated,
                    transition: "background 0.2s ease, border 0.2s ease",
                    padding: 12,
                }}
            >
                <Space direction="vertical" size={4} style={{ width: "100%", minWidth: 0 }}>
                    <Space align="center" size={8} wrap>
                        <Text strong style={{ minWidth: 0, overflowWrap: "anywhere" }}>{tournament.tournamentName}</Text>
                        <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
                    </Space>
                    <Text type="secondary">
                        {dayjs(tournament.createdAt).format("DD.MM.YYYY HH:mm")}
                    </Text>
                    {winner && (
                        <Text>
                            {t("beerpong.tournament.list.winnerLabel")} <strong>{winner}</strong>
                        </Text>
                    )}
                </Space>
            </List.Item>
        );
    };

    const renderCategory = (
        title: string,
        data: Tournament[],
        options?: { emptyText: string; showRefresh?: boolean; showLoading?: boolean }
    ) => {
        const categoryOptions = options ?? { emptyText: t("beerpong.tournament.list.empty.default") };
        let categoryContent: React.ReactNode;
        if (loading && categoryOptions.showLoading) {
            categoryContent = <Skeleton active paragraph={{ rows: 4 }} />;
        } else if (data.length === 0) {
            categoryContent = <Empty description={categoryOptions.emptyText} />;
        } else {
            categoryContent = (
                <List
                    dataSource={data}
                    renderItem={renderItem}
                    split={false}
                    style={{ width: "100%" }}
                />
            );
        }
        return (
        <Card
            key={title}
            size="small"
            title={title}
            style={{ background: "transparent", width: "100%" }}
            extra={
                categoryOptions.showRefresh ? (
                    <Button icon={<ReloadOutlined />} onClick={onReload} disabled={loading}>
                        {t("beerpong.tournament.list.refresh")}
                    </Button>
                ) : undefined
            }
        >
            {categoryContent}
        </Card>
        );
    };

    const activeTournaments = tournaments.filter(
        (tournament) => tournament.status === TournamentStatus.TOURNAMENT_STATUS_ACTIVE
    );
    const completedTournaments = tournaments.filter(
        (tournament) => tournament.status === TournamentStatus.TOURNAMENT_STATUS_COMPLETED
    );
    const otherTournaments = tournaments.filter(
        (tournament) =>
            tournament.status !== TournamentStatus.TOURNAMENT_STATUS_ACTIVE &&
            tournament.status !== TournamentStatus.TOURNAMENT_STATUS_COMPLETED
    );

    return (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            {renderCategory(t("beerpong.tournament.list.categories.active"), activeTournaments, {
                emptyText: t("beerpong.tournament.list.empty.active"),
                showRefresh: true,
                showLoading: true,
            })}
            {renderCategory(t("beerpong.tournament.list.categories.completed"), completedTournaments, {
                emptyText: t("beerpong.tournament.list.empty.completed"),
            })}
            {otherTournaments.length > 0 &&
                renderCategory(t("beerpong.tournament.list.categories.archived"), otherTournaments, {
                    emptyText: t("beerpong.tournament.list.empty.archived"),
                })}
        </Space>
    );
};

export default TournamentList;
